// ./src/components/features/VolunteerSlots/CoordinatePickerDrawer/CoordinatePickerDrawer.tsx
//
// A volunteer slot's "where" as COORDINATES: a pin on the map and an optional coverage radius (m / ft), drawn as a
// circle around it. Rebuilt on the shared `@em/ui` MapPinPicker (owner, 2026-10-23: "rebuild the volunteer picker on
// it") — search a place or an address, click the map, drag the pin, or use this device's location — in place of the
// full-screen CoordinatePickerModal, which had its own copy of all that (and was a modal; input dialogs are drawers).
// The matching address is SHOWN as a hint only: a slot set by coordinates keeps no address.
// ⓘ Local state, not a URL stage: opened from inside the slot drawer's form, it hands the point back to that form.
import React, { useEffect, useMemo, useState } from "react";
import { Group, Input, NumberInput, SegmentedControl, Slider, Stack } from "@mantine/core";
import { MapPin, MapPinPicker } from "@em/ui";
import { useTranslation } from "react-i18next";

import { AppDrawer } from "@/components/generic";
import { useFormSnapshot } from "@/hooks/useFormSnapshot";

export type RadiusUnit = "m" | "ft";

export interface CoordinateValue {
  latitude: number | null;
  longitude: number | null;
  radius: number | null;
  radiusUnit: RadiusUnit;
}

export interface CoordinatePickerDrawerProps {
  opened: boolean;
  onClose: () => void;
  value: CoordinateValue;
  onSave: (value: CoordinateValue) => void;
  title?: string;
}

interface Draft {
  pin: MapPin;
  radius: number | null;
  unit: RadiusUnit;
}

const M_PER_FT = 0.3048;

const toMeters = (radius: number | null, unit: RadiusUnit): number => (radius == null ? 0 : unit === "ft" ? radius * M_PER_FT : radius);
const sliderMax = (unit: RadiusUnit): number => (unit === "ft" ? 6000 : 2000);
const sliderStep = (unit: RadiusUnit): number => (unit === "ft" ? 25 : 10);

const draftOf = (v: CoordinateValue): Draft => ({
  // The address is a hint looked up for the pin — never stored, so it starts empty.
  pin: { latitude: v.latitude ?? null, longitude: v.longitude ?? null, address: "" },
  radius: v.radius ?? null,
  unit: v.radiusUnit || "m",
});

export const CoordinatePickerDrawer: React.FC<CoordinatePickerDrawerProps> = ({ opened, onClose, value, onSave, title }) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const [draft, setDraft] = useState<Draft>(() => draftOf(value));
  // The address is a hint (shown, not saved): it does not make the drawer dirty.
  const compared = useMemo(() => ({ latitude: draft.pin.latitude, longitude: draft.pin.longitude, radius: draft.radius, unit: draft.unit }), [draft]);
  const { isDirty, seedFrom, clear } = useFormSnapshot(compared);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    const seeded = draftOf(value);

    setDraft(seeded);
    seedFrom({ latitude: seeded.pin.latitude, longitude: seeded.pin.longitude, radius: seeded.radius, unit: seeded.unit });
    // Only on opening: the slot does not change while this drawer is over it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const save = () => {
    onSave({
      latitude: draft.pin.latitude,
      longitude: draft.pin.longitude,
      radius: draft.radius && draft.radius > 0 ? draft.radius : null,
      radiusUnit: draft.unit,
    });
    onClose();
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title || t("volunteers:coords.title", { defaultValue: "Set location" })}
      position="right"
      size="xl"
      actionType="save-cancel"
      confirmLabel={t("volunteers:coords.use", { defaultValue: "Use this location" })}
      confirmDisabled={draft.pin.latitude == null || draft.pin.longitude == null}
      onConfirm={save}
      isDirty={isDirty}
      mobileHeightPercent={95}
    >
      {/* Mounted only while open: the map is made on mount and released on close. */}
      {opened ? (
        <Stack gap="md">
          <MapPinPicker
            value={draft.pin}
            onChange={(pin) => setDraft((d) => ({ ...d, pin }))}
            height="min(50vh, 420px)"
            addressMode="show"
            radiusMeters={toMeters(draft.radius, draft.unit)}
          />

          {/* Slider + number + unit share one baseline so nothing sits crooked. */}
          <Input.Wrapper label={t("volunteers:coords.radius", { defaultValue: "Coverage radius" })}>
            <Group gap="sm" mt={4} align="center" wrap="wrap">
              <Slider
                flex={1}
                miw={200}
                min={0}
                max={sliderMax(draft.unit)}
                step={sliderStep(draft.unit)}
                value={draft.radius ?? 0}
                onChange={(radius) => setDraft((d) => ({ ...d, radius }))}
                label={(v) => `${v} ${draft.unit}`}
              />
              <NumberInput
                value={draft.radius ?? ""}
                onChange={(v) => {
                  const radius = v === "" ? null : Math.max(0, Number(v) || 0);

                  setDraft((d) => ({ ...d, radius }));
                }}
                min={0}
                allowNegative={false}
                allowDecimal={false}
                w={120}
                size="md"
                suffix={` ${draft.unit}`}
              />
              <SegmentedControl
                value={draft.unit}
                onChange={(u) => setDraft((d) => ({ ...d, unit: u as RadiusUnit }))}
                data={[
                  { value: "m", label: t("volunteers:coords.metres", { defaultValue: "Metres" }) },
                  { value: "ft", label: t("volunteers:coords.feet", { defaultValue: "Feet" }) },
                ]}
                size="md"
              />
            </Group>
          </Input.Wrapper>
        </Stack>
      ) : null}
    </AppDrawer>
  );
};

export default CoordinatePickerDrawer;
